import { SHOW_AUTH_MODAL } from '@reduxLocal/actions/types';
import { store } from '@reduxLocal/persistState';

/** Minimum gap between offline-related AuthModals (resume/NetInfo flaps spam otherwise). */
export const OFFLINE_DIALOG_COOLDOWN_MS = 60_000;

let lastOfflineDialogAt = 0;
let lastOfflineDialogContent = '';

/**
 * Whether an offline AuthModal may be shown now.
 * Tracks last invoke time + content so background/resume flaps don't bombard the user.
 */
export function shouldShowOfflineDialog(content: string): boolean {
	const now = Date.now();
	const authModal = store.getState()?.modals?.authModal;

	// Already visible with the same (or any) offline notice — do not re-fire haptics/UI.
	if (authModal?.modalDisplayed) {
		const showing = String(authModal?.modalContent || '');
		if (
			showing === content ||
			showing.toLowerCase().includes('offline') ||
			showing.toLowerCase().includes('internet connection')
		) {
			return false;
		}
	}

	if (now - lastOfflineDialogAt < OFFLINE_DIALOG_COOLDOWN_MS) {
		return false;
	}

	return true;
}

export function markOfflineDialogShown(content: string): void {
	lastOfflineDialogAt = Date.now();
	lastOfflineDialogContent = content;
}

/** Last offline dialog content (for diagnostics / tests). */
export function getLastOfflineDialogInvoke(): {
	at: number;
	content: string;
} {
	return { at: lastOfflineDialogAt, content: lastOfflineDialogContent };
}

/**
 * Show offline AuthModal at most once per cooldown window.
 * Returns true if dispatched.
 */
export function dispatchOfflineAuthModal(
	content: string,
	type: 'error' | 'warning' = 'error'
): boolean {
	if (!shouldShowOfflineDialog(content)) {
		if (__DEV__) {
			const since = Date.now() - lastOfflineDialogAt;
			console.log(
				`[offlineDialog] suppressed (${since}ms since last): ${content.slice(0, 80)}`
			);
		}
		return false;
	}

	markOfflineDialogShown(content);
	store.dispatch({
		type: SHOW_AUTH_MODAL,
		data: { content, type },
	});
	return true;
}
